import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, Bug, Lightbulb, Clock, AlertCircle, CheckCircle2, Save,
} from "lucide-react";
import {
  API_BASE_URL,
  getAdminFeedbacksCentralAjuda,
  patchAdminFeedbackCentralAjuda,
} from "../../api";
import { getModuloDocumentacao } from "./content";
import SideBarNavCentralAjuda from "../../componets/SideBarNavCentralAjuda";

const STATUS_OPCOES = [
  { value: "aberto", label: "Aberto", cor: "bg-amber-100 text-amber-700", icone: Clock },
  { value: "em_analise", label: "Em análise", cor: "bg-blue-100 text-blue-700", icone: AlertCircle },
  { value: "resolvido", label: "Resolvido", cor: "bg-emerald-100 text-emerald-700", icone: CheckCircle2 },
];

function CardFeedback({ feedback, onAtualizado }) {
  const [status, setStatus] = useState(feedback.status);
  const [resposta, setResposta] = useState(feedback.resposta_admin || "");
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState(null);

  const modulo = feedback.modulo_id ? getModuloDocumentacao(feedback.modulo_id) : null;
  const alterado = status !== feedback.status || resposta !== (feedback.resposta_admin || "");

  async function salvar() {
    setSalvando(true);
    setErro(null);
    try {
      const atualizado = await patchAdminFeedbackCentralAjuda(feedback.id, { status, resposta_admin: resposta });
      onAtualizado(atualizado);
    } catch (e) {
      setErro(e.message || "Não foi possível salvar.");
    } finally {
      setSalvando(false);
    }
  }

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-2">
        <div className="flex items-center gap-2">
          {feedback.tipo === "bug" ? (
            <Bug className="w-4 h-4 text-red-500" />
          ) : (
            <Lightbulb className="w-4 h-4 text-purple-500" />
          )}
          <span className="font-semibold text-gray-800 text-sm">{feedback.titulo}</span>
          {modulo && (
            <span className="text-xs font-medium text-gray-400 bg-gray-50 border border-gray-200 px-2 py-0.5 rounded-md">
              {modulo.titulo}
            </span>
          )}
        </div>
        <span className="text-xs text-gray-400">
          {feedback.usuario_nome} · {feedback.usuario_email}
        </span>
      </div>

      <p className="text-sm text-gray-500 whitespace-pre-wrap mb-2">{feedback.mensagem}</p>

      {feedback.imagem_path && (
        <a href={`${API_BASE_URL}${feedback.imagem_path}`} target="_blank" rel="noreferrer" className="inline-block mb-3">
          <img src={`${API_BASE_URL}${feedback.imagem_path}`} alt="Anexo do reporte" className="max-h-40 rounded-lg border border-gray-200" />
        </a>
      )}

      <div className="text-xs text-gray-400 mb-4">
        {new Date(feedback.criado_em).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}
      </div>

      <div className="flex flex-wrap items-end gap-3 border-t border-gray-100 pt-4">
        <div>
          <label className="block text-xs font-semibold text-gray-500 uppercase mb-1.5">Status</label>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="px-3 py-2 rounded-lg border border-gray-200 text-sm outline-none focus:ring-2 focus:ring-purple-400"
          >
            {STATUS_OPCOES.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
        </div>
        <div className="flex-1 min-w-[220px]">
          <label className="block text-xs font-semibold text-gray-500 uppercase mb-1.5">Retorno para o usuário (opcional)</label>
          <input
            type="text"
            value={resposta}
            onChange={(e) => setResposta(e.target.value)}
            placeholder="Ex: Corrigido na versão de hoje."
            className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm outline-none focus:ring-2 focus:ring-purple-400"
          />
        </div>
        <button
          onClick={salvar}
          disabled={!alterado || salvando}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-purple-900 to-pink-800 text-white text-sm font-semibold disabled:opacity-40 transition-opacity"
        >
          <Save className="w-4 h-4" /> {salvando ? "Salvando..." : "Salvar"}
        </button>
      </div>
      {erro && <p className="text-xs text-red-500 mt-2">{erro}</p>}
    </div>
  );
}

export default function AdminFeedback() {
  const navigate = useNavigate();
  const [feedbacks, setFeedbacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(null);
  const [filtroStatus, setFiltroStatus] = useState("");
  const [filtroTipo, setFiltroTipo] = useState("");

  async function carregar() {
    setLoading(true);
    setErro(null);
    try {
      const params = new URLSearchParams();
      if (filtroStatus) params.set("status_filtro", filtroStatus);
      if (filtroTipo) params.set("tipo_filtro", filtroTipo);
      const dados = await getAdminFeedbacksCentralAjuda(params.toString());
      setFeedbacks(dados || []);
    } catch (e) {
      setErro(e.message || "Não foi possível carregar os envios.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { carregar(); }, [filtroStatus, filtroTipo]); // eslint-disable-line react-hooks/exhaustive-deps

  function handleAtualizado(atualizado) {
    setFeedbacks((prev) => prev.map((f) => (f.id === atualizado.id ? { ...f, ...atualizado } : f)));
  }

  const resumo = useMemo(() => {
    const contagem = { aberto: 0, em_analise: 0, resolvido: 0 };
    feedbacks.forEach((f) => { contagem[f.status] = (contagem[f.status] || 0) + 1; });
    return contagem;
  }, [feedbacks]);

  return (
    <div style={{ display: "flex", height: "100vh", width: "100vw", overflow: "hidden" }}>
      <SideBarNavCentralAjuda isLight />

      <div className="flex flex-col flex-1 bg-gray-50" style={{ overflow: "auto" }}>
      <header className="bg-gradient-to-r from-purple-900 to-pink-800 text-white px-6 py-4 flex justify-between items-center shadow-md">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate("/admin")} className="p-1.5 rounded-lg hover:bg-white/10 transition">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <span className="text-lg font-bold tracking-tight">Central de Ajuda · Sugestões e Bugs</span>
        </div>
        <button
          onClick={() => navigate("/inicio")}
          className="flex items-center gap-2 px-4 py-2 hover:bg-white/10 rounded-lg text-sm font-bold"
        >
          Menu Principal
        </button>
      </header>

      <main className="flex-1 px-6 py-8 max-w-4xl mx-auto w-full">
        <div className="grid grid-cols-3 gap-4 mb-6">
          {STATUS_OPCOES.map((s) => {
            const Icon = s.icone;
            return (
              <div key={s.value} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 flex items-center gap-4">
                <div className={`p-3 rounded-xl ${s.cor}`}><Icon className="w-5 h-5" /></div>
                <div>
                  <p className="text-xs text-gray-500 font-medium">{s.label}</p>
                  <p className="text-2xl font-bold text-gray-800">{resumo[s.value] || 0}</p>
                </div>
              </div>
            );
          })}
        </div>

        <div className="flex flex-wrap gap-3 mb-6">
          <select
            value={filtroStatus}
            onChange={(e) => setFiltroStatus(e.target.value)}
            className="px-3 py-2 rounded-lg border border-gray-200 text-sm bg-white outline-none focus:ring-2 focus:ring-purple-400"
          >
            <option value="">Todos os status</option>
            {STATUS_OPCOES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
          <select
            value={filtroTipo}
            onChange={(e) => setFiltroTipo(e.target.value)}
            className="px-3 py-2 rounded-lg border border-gray-200 text-sm bg-white outline-none focus:ring-2 focus:ring-purple-400"
          >
            <option value="">Todos os tipos</option>
            <option value="sugestao">Sugestões</option>
            <option value="bug">Bugs</option>
          </select>
        </div>

        {loading && <div className="text-center py-16 text-gray-400 text-sm">Carregando...</div>}
        {!loading && erro && <div className="text-center py-16 text-red-500 text-sm">{erro}</div>}
        {!loading && !erro && feedbacks.length === 0 && (
          <div className="bg-white rounded-2xl border border-gray-100 p-10 text-center text-gray-400 text-sm">
            Nenhum envio encontrado com os filtros selecionados.
          </div>
        )}

        {!loading && !erro && feedbacks.length > 0 && (
          <div className="space-y-3">
            {feedbacks.map((f) => (
              <CardFeedback key={f.id} feedback={f} onAtualizado={handleAtualizado} />
            ))}
          </div>
        )}
      </main>
      </div>
    </div>
  );
}
